import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Breadcrumb navigation component
 */
const Breadcrumbs = ({ items, className = '' }) => {
  const { t } = useTranslation();

  if (!items || items.length === 0) {
    return null;
  }

  // Icon fallbacks for test environments where lucide-react may be partially mocked
  const ChevronIcon = (props) => (typeof ChevronRight === 'function' ? <ChevronRight {...props} /> : <span {...props} aria-hidden="true">›</span>);
  const HomeIcon = (props) => (typeof Home === 'function' ? <Home {...props} /> : <span {...props} aria-hidden="true">⌂</span>);

  const truncateLabel = (text, maxLength = 20) => {
    if (typeof text !== 'string') return text;
    return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
  };

  return (
    <nav className={`flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {/* Home link */}
        <li>
          <Link
            to="/"
            className="flex items-center hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            aria-label={t('nav.home')}
          >
            <HomeIcon className="w-4 h-4" />
            <span className="sr-only">{t('nav.home')}</span>
          </Link>
        </li>

        {/* Breadcrumb items */}
        {items.map((item, index) => (
          <li key={item.path || index} className="flex items-center">
            <ChevronIcon className="w-4 h-4 mx-2" aria-hidden="true" />
            {index === items.length - 1 ? (
              // Current page - not a link
              <span
                className="font-medium text-gray-900 dark:text-white truncate max-w-[200px]"
                aria-current="page"
                aria-hidden="true"
                title={item.label}
              >
                {truncateLabel(item.label)}
              </span>
            ) : (
              // Previous pages - link if path provided, else plain text
              item.path ? (
                <Link
                  to={item.path}
                  className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors truncate max-w-[200px]"
                  title={item.label}
                >
                  {item.label}
                </Link>
              ) : (
                <span className="truncate max-w-[200px]" title={item.label}>
                  {item.label}
                </span>
              )
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

/**
 * Hook to generate breadcrumbs based on current location
 */
export const useBreadcrumbs = (currentPage, data) => {
  const { t } = useTranslation();

  const generateBreadcrumbs = React.useCallback((pageType, pageData) => {
    const breadcrumbs = [];
    const itemSlugs = data && data.itemSlugs ? data.itemSlugs : {};
    const personSlugs = data && data.personSlugs ? data.personSlugs : {};

    switch (pageType) {
      case 'person':
        breadcrumbs.push({
          label: t('nav.people') // No route defined; render as text
        });
        if (pageData) {
          // Prefer stored person slug mapping when available
          let personId = pageData.id;
          const key = pageData.name_jp && pageData.name_romaji ? `${pageData.name_jp} (${pageData.name_romaji})` : null;
          if (key && personSlugs[key]) {
            personId = personSlugs[key];
          }
          breadcrumbs.push({
            label: pageData.name_romaji || pageData.name_jp,
            path: `/person/${personId}` // Current page, no link needed
          });
        }
        break;

      case 'item':
        breadcrumbs.push({
          label: t('nav.items') // No route defined; render as text
        });
        if (pageData) {
          const itemName = typeof pageData.name === 'object'
            ? (pageData.name.en || pageData.name.jp)
            : pageData.name || 'Unknown Item';
          // Prefer stored item slug mapping when available
          const itemId = itemSlugs[pageData.name?.jp || pageData.name] || pageData.id;
          breadcrumbs.push({
            label: itemName,
            path: `/item/${itemId}` // Current page, no link needed
          });
        }
        break;

      case 'timeline':
        breadcrumbs.push({
          label: t('nav.timeline'),
          path: '/timeline'
        });
        break;

      case 'network':
        breadcrumbs.push({
          label: t('nav.network'),
          path: '/network'
        });
        break;

      default:
        // No breadcrumbs for home or unknown pages
        break;
    }

    return breadcrumbs;
  }, [t]);

  return React.useMemo(() => {
    return generateBreadcrumbs(currentPage.type, currentPage.data);
  }, [generateBreadcrumbs, currentPage]);
};

export default Breadcrumbs;